import { createForm } from "@tanstack/solid-form";
import { format } from "date-fns";
import { BiRegularChevronDown } from "solid-icons/bi";
import { createSignal, Show } from "solid-js";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { TagInput } from "@/components/shared/input";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  Input,
} from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  EFFORT,
  type Effort,
  EXPERIENCE_LEVEL,
  type ExperienceLevel,
  type RemotePolicy,
} from "@/domains/job-offer/create/constants";
import { parseDateOrNull } from "@/libs/date";
import {
  MAX_SKILL_TERM_LENGTH,
  MAX_SKILL_TERMS,
  optionalSemanticSearchSchema,
  optionalSkillTermsSchema,
} from "@/libs/skill-input";
import type { FreelancerFilterFormValues } from "../utils/filterParams";
import { CategorySubcategoryField } from "./subcategory-filter/CategorySubcategoryField";

const PRICE_MIN = 0;
const PRICE_MAX = 1000;
const PRICE_STEP = 25;

const WORK_PREFERENCE_OPTIONS = ["remote", "onsite", "hybrid"] as const;

const formSchema = z.object({
  search: optionalSemanticSearchSchema,
  job_category: z.string().optional(),
  job_subcategory: z.array(z.string()).optional(),
  skills: optionalSkillTermsSchema,
  work_preference: z.string().optional(),
  price_per_day_min: z.string().optional(),
  price_per_day_max: z.string().optional(),
  weekly_hours: z.string().optional(),
  available_from: z.string().optional(),
  experience_level: z.string().optional(),
  job_offer_id: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export type FreelancerFiltersFormProps = {
  defaultValues: FreelancerFilterFormValues;
  onSubmit: (values: FreelancerFilterFormValues) => void;
  customFooter?: boolean;
  onReset: () => void;
  disablePriceFilter?: boolean;
  /**
   * Opt-in: hide the experience-level field entirely. Default `false` keeps the
   * active-search form byte-for-byte unchanged; the Talent Pool passes `true`
   * because the BE accepts `experience_level` but does not filter on it.
   */
  hideExperienceFilter?: boolean;
  /** Which page hosts the form: picks the behaviour hint of the category field. */
  surface?: "search" | "pool";
  /** Accessor for the offer-subcategories hint; forwarded to the category field. */
  offerContext?: () => {
    active: boolean;
    offer: { category: string; subcategories: string[] } | null;
  };
  /** The form sits inside the «Filtri» modal sheet. */
  inSheet?: boolean;
};

export function FreelancerFiltersForm(props: FreelancerFiltersFormProps) {
  const { t } = useI18n();
  const [showAdvanced, setShowAdvanced] = createSignal(false);

  const form = createForm(() => ({
    defaultValues: {
      search: props.defaultValues.search ?? "",
      job_category: props.defaultValues.job_category ?? "",
      job_subcategory: props.defaultValues.job_subcategory ?? [],
      skills: props.defaultValues.skills ?? [],
      work_preference: props.defaultValues.work_preference ?? "",
      price_per_day_min: props.defaultValues.price_per_day_min ?? "",
      price_per_day_max: props.defaultValues.price_per_day_max ?? "",
      weekly_hours: props.defaultValues.weekly_hours ?? "",
      available_from: props.defaultValues.available_from ?? "",
      experience_level: props.defaultValues.experience_level ?? "",
      job_offer_id: props.defaultValues.job_offer_id ?? "",
    } as FormValues,
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (parsed.success) {
        props.onSubmit(parsed.data as FreelancerFilterFormValues);
      }
    },
  }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      class="space-y-4"
    >
      <form.Field
        name="search"
        validators={{ onChange: formSchema.shape.search }}
      >
        {(field) => (
          <FieldContainer>
            <FieldContainerLabel for={field().name}>
              {t("company.search.freelancers.result.filters.search.label")}
            </FieldContainerLabel>
            <Input
              id={field().name}
              name={field().name}
              type="text"
              value={field().state.value ?? ""}
              onInput={(e) => field().handleChange(e.target.value)}
              onBlur={() => field().handleBlur()}
              onChange={(e) => field().handleChange(e.target.value)}
              placeholder={t(
                "company.search.freelancers.result.filters.search.placeholder",
              )}
            />
            <FieldInfo field={field()} />
          </FieldContainer>
        )}
      </form.Field>

      <form.Field
        name="job_category"
        validators={{ onChange: formSchema.shape.job_category }}
      >
        {(categoryField) => (
          <form.Field name="job_subcategory" mode="array">
            {(subcategoryField) => (
              <FieldContainer>
                <CategorySubcategoryField
                  category={categoryField().state.value ?? ""}
                  subcategories={subcategoryField().state.value ?? []}
                  surface={props.surface ?? "search"}
                  inSheet={props.inSheet}
                  offerContext={props.offerContext}
                  onChange={(v) => {
                    form.setFieldValue("job_category", v.category);
                    form.setFieldValue("job_subcategory", v.subcategories);
                  }}
                />
                <FieldInfo field={categoryField()} />
              </FieldContainer>
            )}
          </form.Field>
        )}
      </form.Field>

      <form.Field
        name="skills"
        mode="array"
        validators={{ onChange: formSchema.shape.skills }}
      >
        {(field) => (
          <FieldContainer>
            <FieldContainerLabel for="skills">
              {t("company.search.freelancers.result.filters.skills.label")}
            </FieldContainerLabel>
            <TagInput
              fieldApi={field()}
              class="bg-white"
              maxTags={MAX_SKILL_TERMS}
              maxTagLength={MAX_SKILL_TERM_LENGTH}
              placeholder={t(
                "company.search.freelancers.result.filters.skills.placeholder",
              )}
            />
            <FieldInfo field={field()} />
          </FieldContainer>
        )}
      </form.Field>

      <form.Field
        name="work_preference"
        validators={{ onChange: formSchema.shape.work_preference }}
      >
        {(field) => (
          <FieldContainer>
            <FieldContainerLabel for={field().name}>
              {t(
                "company.search.freelancers.result.filters.work_preference.label",
              )}
            </FieldContainerLabel>
            <Select
              value={field().state.value ?? undefined}
              onChange={(value) => field().handleChange(value ?? "")}
              options={[...WORK_PREFERENCE_OPTIONS]}
              placeholder={t(
                "company.search.freelancers.result.filters.work_preference.placeholder",
              )}
              itemComponent={(item) => (
                <SelectItem item={item.item}>
                  <span>
                    {t(
                      `common.remote_policy_options.${item.item.rawValue as RemotePolicy}`,
                    )}
                  </span>
                </SelectItem>
              )}
            >
              <SelectTrigger class="bg-white">
                <SelectValue>
                  <Show
                    when={field().state.value}
                    fallback={
                      <span>
                        {t(
                          "company.search.freelancers.result.filters.work_preference.placeholder",
                        )}
                      </span>
                    }
                  >
                    {(val) => (
                      <span>
                        {val()
                          ? t(
                              `common.remote_policy_options.${val() as RemotePolicy}`,
                            )
                          : t(
                              "company.search.freelancers.result.filters.work_preference.placeholder",
                            )}
                      </span>
                    )}
                  </Show>
                </SelectValue>
              </SelectTrigger>
              <SelectContent />
            </Select>
            <FieldInfo field={field()} />
          </FieldContainer>
        )}
      </form.Field>

      <form.Field
        name="price_per_day_min"
        validators={{ onChange: formSchema.shape.price_per_day_min }}
      >
        {(fieldMin) => (
          <form.Field
            name="price_per_day_max"
            validators={{ onChange: formSchema.shape.price_per_day_max }}
          >
            {(fieldMax) => {
              const minVal = () => {
                const v = fieldMin().state.value;
                const n = v ? Number.parseInt(v, 10) : PRICE_MIN;
                return Number.isNaN(n)
                  ? PRICE_MIN
                  : Math.min(Math.max(n, PRICE_MIN), PRICE_MAX);
              };
              const maxVal = () => {
                const v = fieldMax().state.value;
                const n = v ? Number.parseInt(v, 10) : PRICE_MAX;
                return Number.isNaN(n)
                  ? PRICE_MAX
                  : Math.min(Math.max(n, PRICE_MIN), PRICE_MAX);
              };
              const safeMin = () => Math.min(minVal(), maxVal());
              const safeMax = () => Math.max(minVal(), maxVal());
              const hintId = "price-range-disabled-hint";
              const disabled = () => props.disablePriceFilter === true;
              return (
                <FieldContainer>
                  <FieldContainerLabel>
                    {t(
                      "company.search.freelancers.result.filters.price_range.label",
                    )}
                  </FieldContainerLabel>
                  <div
                    class="space-y-3 pt-1"
                    classList={{ "opacity-60": disabled() }}
                  >
                    <div class="flex items-center justify-between gap-2 text-sm text-muted-foreground">
                      <span>{safeMin()} €/g</span>
                      <span>{safeMax()} €/g</span>
                    </div>
                    <div class="flex gap-2 items-center">
                      <input
                        type="range"
                        min={PRICE_MIN}
                        max={PRICE_MAX}
                        step={PRICE_STEP}
                        value={safeMin()}
                        id={fieldMin().name}
                        name={fieldMin().name}
                        disabled={disabled()}
                        aria-describedby={disabled() ? hintId : undefined}
                        onInput={(e) => {
                          const v = Number(e.currentTarget.value);
                          fieldMin().handleChange(String(v));
                          if (v > maxVal()) fieldMax().handleChange(String(v));
                        }}
                        class="flex-1 h-2 rounded-lg appearance-none cursor-pointer bg-neutral-200 accent-primary disabled:cursor-not-allowed"
                        aria-label={t(
                          "company.search.freelancers.result.filters.price_per_day_min.label",
                        )}
                      />
                      <input
                        type="range"
                        min={PRICE_MIN}
                        max={PRICE_MAX}
                        step={PRICE_STEP}
                        value={safeMax()}
                        id={fieldMax().name}
                        name={fieldMax().name}
                        disabled={disabled()}
                        aria-describedby={disabled() ? hintId : undefined}
                        onInput={(e) => {
                          const v = Number(e.currentTarget.value);
                          fieldMax().handleChange(String(v));
                          if (v < minVal()) fieldMin().handleChange(String(v));
                        }}
                        class="flex-1 h-2 rounded-lg appearance-none cursor-pointer bg-neutral-200 accent-primary disabled:cursor-not-allowed"
                        aria-label={t(
                          "company.search.freelancers.result.filters.price_per_day_max.label",
                        )}
                      />
                    </div>
                    <Show when={disabled()}>
                      <p id={hintId} class="text-xs text-muted-foreground">
                        {t(
                          "company.search.freelancers.result.filters.price_range.disabled_hint",
                        )}
                      </p>
                    </Show>
                  </div>
                  <FieldInfo field={fieldMin()} />
                </FieldContainer>
              );
            }}
          </form.Field>
        )}
      </form.Field>

      <div class="border-t border-neutral-200 pt-4">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced())}
          class="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 py-1"
        >
          {t("company.search.freelancers.result.filters.advanced_title")}
          <BiRegularChevronDown
            class="w-4 h-4 transition-transform duration-200"
            classList={{ "rotate-180": showAdvanced() }}
          />
        </button>
        <Show when={showAdvanced()}>
          <div class="space-y-4 mt-4">
            <form.Field
              name="weekly_hours"
              validators={{ onChange: formSchema.shape.weekly_hours }}
            >
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for={field().name}>
                    {t(
                      "company.search.freelancers.result.filters.weekly_hours.label",
                    )}
                  </FieldContainerLabel>
                  <Select
                    value={field().state.value ?? undefined}
                    onChange={(value) => field().handleChange(value ?? "")}
                    options={EFFORT}
                    placeholder={t(
                      "company.search.freelancers.result.filters.weekly_hours.placeholder",
                    )}
                    itemComponent={(item) => (
                      <SelectItem item={item.item}>
                        {t(
                          `common.effort_options.${item.item.rawValue as Effort}`,
                        )}
                      </SelectItem>
                    )}
                  >
                    <SelectTrigger class="bg-white">
                      <SelectValue>
                        <Show
                          when={field().state.value}
                          fallback={
                            <span>
                              {t(
                                "company.search.freelancers.result.filters.weekly_hours.placeholder",
                              )}
                            </span>
                          }
                        >
                          <span>
                            {field().state.value
                              ? t(
                                  `common.effort_options.${field().state.value as Effort}`,
                                )
                              : t(
                                  "company.search.freelancers.result.filters.weekly_hours.placeholder",
                                )}
                          </span>
                        </Show>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent />
                  </Select>
                  <FieldInfo field={field()} />
                </FieldContainer>
              )}
            </form.Field>

            <form.Field
              name="available_from"
              validators={{ onChange: formSchema.shape.available_from }}
            >
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for={field().name}>
                    {t(
                      "company.search.freelancers.result.filters.available_from.label",
                    )}
                  </FieldContainerLabel>
                  <DatePicker
                    id={field().name}
                    value={parseDateOrNull(field().state.value ?? "")}
                    onChange={(date) =>
                      field().handleChange(
                        date ? format(date, "yyyy-MM-dd") : "",
                      )
                    }
                    placeholder={t(
                      "company.search.freelancers.result.filters.available_from.placeholder",
                    )}
                  />
                  <FieldInfo field={field()} />
                </FieldContainer>
              )}
            </form.Field>

            <Show when={!props.hideExperienceFilter}>
              <form.Field
                name="experience_level"
                validators={{ onChange: formSchema.shape.experience_level }}
              >
                {(field) => (
                  <FieldContainer>
                    <FieldContainerLabel for={field().name}>
                      {t(
                        "company.search.freelancers.result.filters.experience_level.label",
                      )}
                    </FieldContainerLabel>
                    <Select
                      value={field().state.value ?? undefined}
                      onChange={(value) => field().handleChange(value ?? "")}
                      options={EXPERIENCE_LEVEL}
                      placeholder={t(
                        "company.search.freelancers.result.filters.experience_level.placeholder",
                      )}
                      itemComponent={(item) => (
                        <SelectItem item={item.item}>
                          {t(
                            `common.experience_level_options.${item.item.rawValue as ExperienceLevel}`,
                          )}
                        </SelectItem>
                      )}
                    >
                      <SelectTrigger class="bg-white">
                        <SelectValue>
                          <Show
                            when={field().state.value}
                            fallback={
                              <span>
                                {t(
                                  "company.search.freelancers.result.filters.experience_level.placeholder",
                                )}
                              </span>
                            }
                          >
                            <span>
                              {field().state.value
                                ? t(
                                    `common.experience_level_options.${field().state.value as ExperienceLevel}`,
                                  )
                                : t(
                                    "company.search.freelancers.result.filters.experience_level.placeholder",
                                  )}
                            </span>
                          </Show>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent />
                    </Select>
                    <FieldInfo field={field()} />
                  </FieldContainer>
                )}
              </form.Field>
            </Show>
          </div>
        </Show>
      </div>

      <Show when={!props.customFooter}>
        <div class="flex flex-col gap-2 mt-4">
          <Button
            type="button"
            variant="outline"
            class="w-full"
            onClick={() => props.onReset()}
          >
            {t("company.search.freelancers.result.filters.reset")}
          </Button>
          <Button
            type="button"
            class="w-full"
            onClick={() => form.handleSubmit()}
          >
            {t("company.search.freelancers.result.filters.apply")}
          </Button>
        </div>
      </Show>
    </form>
  );
}
